import { calculatePercentage, financial } from "@shared/utils/helper";
import {
  DollarSign,
  PackageIcon,
  TrendingUp,
  TrendingDown,
  Banknote,
  Percent,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { Skeleton } from "@shared/components/ui/skeleton";
import { Badge } from "@shared/components/ui/badge";

export const CardElement = ({ title, value, icon, subtitle, trend }) => {
  const { t } = useTranslation();
  return (
    <div className="p-6 bg-background text-foreground border rounded-xl shadow-sm hover:shadow-md transition-shadow">
      <div className="flex flex-row items-center justify-between space-y-0 pb-2">
        <div className="flex flex-col space-y-1">
          <h2 className="text-sm font-medium text-muted-foreground">
            {t(title)}
          </h2>
          {subtitle && (
            <p className="text-xs text-muted-foreground">{subtitle}</p>
          )}
        </div>
        <div className="flex items-center gap-2">
          {trend && (
            <Badge
              variant={trend > 0 ? "default" : "secondary"}
              className="text-xs"
            >
              {trend > 0 ? (
                <TrendingUp className="h-3 w-3 mr-1" />
              ) : (
                <TrendingDown className="h-3 w-3 mr-1" />
              )}
              {Math.abs(trend)}%
            </Badge>
          )}
          {icon}
        </div>
      </div>
      <div>
        <div className="text-2xl font-bold">{value}</div>
      </div>
    </div>
  );
};

export const DashboardCardsSkeleton = () => (
  <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
    {[...Array(8)].map((_, i) => (
      <div
        key={i}
        className="p-6 bg-background text-foreground border rounded-xl"
      >
        <div className="flex flex-row items-center justify-between space-y-0 pb-2">
          <Skeleton className="h-5 w-[100px]" />
          <Skeleton className="h-6 w-6 rounded-full" />{" "}
        </div>
        <div>
          <Skeleton className="h-8 w-[120px] mt-2" />
        </div>
      </div>
    ))}
  </div>
);

const DashboardCards = ({ data }) => {
  const { t } = useTranslation();

  // Use totalPackagesInRange (filtered by date) - show 0 if not available
  const totalPackagesInRange = data.packages.totalPackagesInRange || 0;

  const deliveryRate = calculatePercentage(
    data.packages.totalDelivered,
    totalPackagesInRange
  );

  const CardData = [
    {
      title: "dashboard.totalPackages",
      value: totalPackagesInRange,
      subtitle: `${data.packages.totalDelivered} ${t("dashboard.delivered")}`,
      icon: <PackageIcon className="h-5 w-5 text-blue-500" />,
    },
    {
      title: "dashboard.tauxDelivery",
      value: deliveryRate.toFixed(1) + "%",
      subtitle: t("dashboard.deliverySuccessRate"),
      icon: <Percent className="h-5 w-5 text-green-500" />,
      trend: deliveryRate > 80 ? 5 : -2, // Example trend
    },
    {
      title: "dashboard.totalrevenue",
      value: financial(data.amounts.totalRevenue) + " " + t("main.currency"),
      subtitle: t("dashboard.grossRevenue"),
      icon: <DollarSign className="h-5 w-5 text-emerald-500" />,
    },
    {
      title: "dashboard.totalCost",
      value: financial(data.amounts.totalCost) + " " + t("main.currency"),
      subtitle: t("dashboard.operatingCosts"),
      icon: <DollarSign className="h-5 w-5 text-red-500" />,
    },
    {
      title: "dashboard.totalInvoices",
      value: data.invoices.totalInvoices,
      subtitle: t("dashboard.generatedInvoices"),
      icon: <Banknote className="h-5 w-5 text-purple-500" />,
    },
    {
      title: "dashboard.InvoicePaid",
      value: financial(data.profit.paid) + " " + t("main.currency"),
      subtitle: t("dashboard.collectedPayments"),
      icon: <TrendingUp className="h-5 w-5 text-green-500" />,
    },
    {
      title: "dashboard.pendingProfit",
      value:
        financial(
          data.amounts.totalRevenue - data.amounts.totalCost - data.profit.paid
        ) +
        " " +
        t("main.currency"),
      subtitle: t("dashboard.pendingCollection"),
      icon: <TrendingDown className="h-5 w-5 text-orange-500" />,
    },
    {
      title: "dashboard.netProfit",
      value:
        financial(data.amounts.totalRevenue - data.amounts.totalCost) +
        " " +
        t("main.currency"),
      subtitle: t("dashboard.totalProfit"),
      icon: <DollarSign className="h-5 w-5 text-indigo-500" />,
    },
  ];

  return (
    <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
      {CardData.map((card) => (
        <CardElement key={card.title} {...card} />
      ))}
    </div>
  );
};

export default DashboardCards;
